import {
  ENV,
  isLocalhost,
  isPrDeployment,
} from '@backoffice-frontend/environment'
import { useCallback, useEffect, useState } from 'react'
import store from 'store'
import {
  CognitoIdentityProviderStages,
  DEFAULT_ORGANIZATION,
  MANUALLY_SELECTED_ORGANIZATION,
} from './constants'

function getOrganizationFromSubdomain(): string | null {
  if (isLocalhost || isPrDeployment) {
    const selectedOrg = store.get(MANUALLY_SELECTED_ORGANIZATION)
    return selectedOrg || DEFAULT_ORGANIZATION
  }

  // Extract organization from subdomain
  const subdomain = window.location.hostname.split('.')[0]

  // Invalid URL structure (no subdomain)
  if (!subdomain || subdomain === window.location.hostname) {
    return null
  }

  // Legacy subdomain - maintain backwards compatibility
  if (subdomain === 'frontend') {
    return DEFAULT_ORGANIZATION
  }

  // New pattern: subdomain is the organization slug
  return subdomain
}

function getCognitoProviderAPIUrl(organization: string | null): string {
  const baseUrl = CognitoIdentityProviderStages[ENV]
  return `${baseUrl}/cognito-identity-provider/${organization}`
}

export function useAuthProvider() {
  const [cognitoIdentityProviderName, setCognitoIdentityProviderName] =
    useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchCognitoIdentityProvider = useCallback(async () => {
    setLoading(true)
    setCognitoIdentityProviderName(null)
    try {
      const organization = getOrganizationFromSubdomain()
      const endpoint = getCognitoProviderAPIUrl(organization)

      const response = await fetch(endpoint)

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error ?? 'Failed to fetch auth provider')
      }

      const data = await response.json()
      setCognitoIdentityProviderName(data.cognitoIdentityProviderName)
    } catch (err) {
      console.error('Error fetching Cognito client:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCognitoIdentityProvider()
  }, [fetchCognitoIdentityProvider])

  return {
    cognitoIdentityProviderName,
    loading,
    refetch: fetchCognitoIdentityProvider,
  }
}
